import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  HardwareManagementTypStore,
  SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_TYP
} from '../../../hardware-management.shared';
import {map} from 'rxjs/operators';
import {UntypedFormControl, UntypedFormGroup, FormGroupDirective, ReactiveFormsModule} from '@angular/forms';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  Validators,
} from '@softline/ui-core';
import { EntityInputComponent } from '@softline/ui-core/entity-input';
import {FocusMonitor} from '@angular/cdk/a11y';
import {Store} from '@softline/core';
import {showRequestErrors} from '@softline/application';
import {HardwareTyp} from '../../../types/hardware-typ';
import {CommonModule} from '@angular/common';
import {InputBaseComponent} from '../../input-base/input-base.component';

import { InputComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-typ-input',
    imports: [CommonModule, InputBaseComponent, ReactiveFormsModule,
    InputComponent,
    TranslatePipe],
    templateUrl: './typ-input.component.html',
    styleUrls: ['./typ-input.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TypInputComponent {

  @Input() readonly = false;
  @Input() required = false;

  readonly typInEditing$ = new BehaviorSubject<HardwareTyp | null>(null);

  readonly products$ = this.store.observe(
    SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_TYP,
    HardwareManagementTypStore.getters.all
  ).pipe(map(o => o as HardwareTyp[]));

  get formGroup(): UntypedFormGroup {
    return this.parentFormGroup.form;
  }

  readonly typEditingForm = new UntypedFormGroup({
    typ: new UntypedFormControl(null, Validators.required()),
    bezeichnung: new UntypedFormControl(null),
    beschreibung: new UntypedFormControl(null)
  });

  constructor(
    private readonly parentFormGroup: FormGroupDirective,
    private readonly focusMonitor: FocusMonitor,
    private store: Store
  ) { }

  select(typ: HardwareTyp | null | undefined): void {
    if(!typ)
      return;
    this.formGroup.patchValue({ hardwaretyp: typ });
  }

  edit(entity: HardwareTyp | null | undefined): void {
    if(!entity)
      return;
    this.typInEditing$.next(entity);
    this.typEditingForm.patchValue(entity);
  }

  resetForm(typ?: HardwareTyp | null | undefined): void {
    if (!typ) {
      this.typEditingForm.reset();
      return;
    }

    this.typEditingForm.reset(typ);
  }

  async saveEdit(entity: HardwareTyp): Promise<void> {
    const updatedEntity = {
      ...entity,
      ...this.typEditingForm.value
    };

    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_TYP,
        HardwareManagementTypStore.actions.update,
        { entity: updatedEntity }
      );

      this.select(result);
      this.typEditingForm.reset();
      this.typInEditing$.next(null);

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Hardware-Typ wurde erfolgreich aktualisiert'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  async create(entity: HardwareTyp): Promise<void> {
    try {
      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_HARDWARE_MANAGEMENT_TYP,
        HardwareManagementTypStore.actions.create,
        { entity }
      );

      this.select(result);
      this.typEditingForm.reset();

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        'Hardware-Typ wurde erfolgreich angelegt'
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  valueChanged(newValue: HardwareTyp | undefined | null): void {
    if (!newValue) {
      this.typEditingForm.reset();
      this.typInEditing$.next(null);
    }

    if (newValue && this.formGroup.value?.hardwaretyp?.id !== newValue?.id) {
      this.formGroup.patchValue({ hardwaretyp: newValue }, { onlySelf: true });
    }
  }

  canceled(entityInput?: EntityInputComponent<any>): void {
    this.formGroup.patchValue({ hardwaretyp: null });
    this.typEditingForm.reset();
    this.typInEditing$.next(null);

    if (entityInput) {
      // remove any existing text of the input (reset)
      entityInput.onInput('');
      entityInput.input = '';
      entityInput.inputComponent.nativeElement.value = '';
    }
  }

  closed(): void {
    this.typEditingForm.reset();
  }

  // We have to use an arrow-function in order to preserve `this` context
  readonly filterTypes = (types: HardwareTyp[], filter: string): HardwareTyp[] => {
    if (!filter || filter?.length < 1) {
      return types;
    }

    return types.filter(typ => {
      return typ.typ.toLowerCase().includes(filter)
        || typ.bezeichnung.toLowerCase().includes(filter)
        || typ.beschreibung.toLowerCase().includes(filter);
    });
  }
}
